// Shared audio-waveform peak extraction for the dubbing timeline — the media
// track (one source video) and the voiceover track (many block audios) decode +
// bucket peaks the same way; only the density / caps differ. Framework-free so
// the peak math is unit-testable and the decode can be driven by either a
// single-url hook or a bounded worker pool.

import { localSourceOf } from '@src/services/mediaExtractor';

// Byte cap before reading a file into memory (guards a file unexpectedly large
// for its duration). Raised to fit a ~90-minute source; still a hard guard so a
// genuinely huge file can't blow up the tab.
const MAX_WAVEFORM_BYTES = 1024 * 1024 * 1024;

// Decode at this low rate: the peak scan then runs over far fewer samples and
// the resampling happens off the main thread. Ample detail for a waveform.
const WAVEFORM_SAMPLE_RATE = 8000;

const AudioContextClass =
  typeof window !== 'undefined' &&
  (window.AudioContext || window.webkitAudioContext);

// Decode context at the low rate; fall back to default if the option isn't taken.
const createDecodeContext = () => {
  try {
    return new AudioContextClass({ sampleRate: WAVEFORM_SAMPLE_RATE });
  } catch (error) {
    return new AudioContextClass();
  }
};

// Duration from a metadata-only <audio> load, so an over-long file is skipped
// before downloading it. NaN on error = skip.
const getMediaDuration = (url) =>
  new Promise((resolve) => {
    const audio = document.createElement('audio');
    // Match the CORS mode of the other loaders of this url (preview <video>,
    // thumbnail extractor) so S3 only ever caches the ACAO copy — a no-CORS
    // probe here would poison the cache and CORS-block those.
    audio.crossOrigin = 'anonymous';
    audio.preload = 'metadata';
    const finish = (value) => {
      audio.removeAttribute('src');
      audio.load();
      resolve(value);
    };
    audio.addEventListener('loadedmetadata', () => finish(audio.duration), {
      once: true,
    });
    audio.addEventListener('error', () => finish(NaN), { once: true });
    audio.src = url;
  });

/**
 * Loudest sample per uniform-time bucket, normalized so quiet audio still fills
 * the band. Uniform buckets keep a leading slice time-accurate (the consumer
 * trims to the heard window). Pure.
 */
export const computePeaks = (channel, count) => {
  const bucketSize = Math.floor(channel.length / count) || 1;
  const peaks = new Array(count);
  let max = 0;
  for (let i = 0; i < count; i += 1) {
    const start = i * bucketSize;
    let peak = 0;
    for (let j = 0; j < bucketSize; j += 1) {
      const value = Math.abs(channel[start + j] || 0);
      if (value > peak) peak = value;
    }
    peaks[i] = peak;
    if (peak > max) max = peak;
  }
  if (max > 0) for (let i = 0; i < count; i += 1) peaks[i] /= max;
  return peaks;
};

// Bucket count for `duration` seconds at `peaksPerSecond`, clamped to the
// [minPeaks, maxPeaks] band. Pure.
export const getPeakCount = (
  duration,
  { peaksPerSecond, minPeaks, maxPeaks },
) =>
  Math.min(maxPeaks, Math.max(minPeaks, Math.round(duration * peaksPerSecond)));

/**
 * Fetch + Web Audio decode `url` into normalized peaks + duration at a density
 * of `peaksPerSecond` (clamped to [minPeaks, maxPeaks]). With `maxDurationSec`,
 * a metadata probe skips over-long files before downloading. Resolves null when
 * skipped or on any error (CORS, unsupported audio, over the caps) — callers
 * omit the waveform. Honors `signal` (aborts the fetch).
 *
 * @returns {Promise<{ peaks: number[], duration: number } | null>}
 */
export const decodeAudioPeaks = async (
  url,
  {
    peaksPerSecond,
    minPeaks,
    maxPeaks,
    maxDurationSec,
    signal,
    reload,
    getBytes,
  } = {},
) => {
  if (!url || !AudioContextClass) return null;

  // Duration cap first — cheapest way to skip a file before fetching it.
  if (maxDurationSec) {
    const probe = await getMediaDuration(url);
    if (!Number.isFinite(probe) || probe > maxDurationSec) return null;
  }

  let ctx;
  try {
    // `reload` bypasses the HTTP cache: the same url is also loaded via <audio>
    // (playback/trim) WITHOUT an Origin header, so S3 caches a copy with no
    // `Access-Control-Allow-Origin`. Reusing it for this CORS fetch throws an
    // intermittent CORS error — forcing a fresh network read always gets the
    // Origin-varied (ACAO) response. Harmless when there's nothing to reuse.
    // `getBytes` is how the media track hands over the download the filmstrip is
    // already doing, so the same 170MB is not pulled twice for one video. It
    // comes back empty when there is no such download — the strip was already
    // cached, or this is a music clip — and then the file is fetched here.
    let buffer;
    // A url uploaded from this machine reads from disk (a File is a Blob),
    // ahead of fetching the whole file back down.
    const shared =
      (getBytes ? await getBytes(url) : null) || localSourceOf(url);
    if (shared) {
      if (shared.size > MAX_WAVEFORM_BYTES) return null;
      buffer = await shared.arrayBuffer();
    } else {
      const res = await fetch(url, {
        signal,
        cache: reload ? 'reload' : 'default',
      });
      if (Number(res.headers.get('content-length')) > MAX_WAVEFORM_BYTES) {
        return null;
      }
      buffer = await res.arrayBuffer();
    }
    ctx = createDecodeContext();
    const audio = await ctx.decodeAudioData(buffer);
    const count = getPeakCount(audio.duration, {
      peaksPerSecond,
      minPeaks,
      maxPeaks,
    });
    return {
      peaks: computePeaks(audio.getChannelData(0), count),
      duration: audio.duration,
    };
  } catch (error) {
    return null;
  } finally {
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
  }
};

// Each decode opens an AudioContext, and browsers only allow a few at once
// (~6 in Chrome). Too many clips decoding together would error and hog memory,
// so run at most this many; the rest wait. One limit shared by every track.
const MAX_CONCURRENT_DECODES = 3;
// url -> running decode, so the same url is decoded only once even if several
// clips ask at the same time (e.g. a duplicated clip).
const inFlightDecodes = new Map();
const waitingDecodes = [];
let runningDecodes = 0;

const pumpDecodes = () => {
  // One decode finished (or failed): free its slot and start the next waiter.
  const onSettled = () => {
    runningDecodes -= 1;
    pumpDecodes();
  };
  while (runningDecodes < MAX_CONCURRENT_DECODES && waitingDecodes.length) {
    runningDecodes += 1;
    const startDecode = waitingDecodes.shift();
    startDecode().then(onSettled, onSettled);
  }
};

/**
 * decodeAudioPeaks, but queued so only a few run at once and the same url is
 * decoded once. Same result ({ peaks, duration } | null); never rejects. Use
 * this instead of decodeAudioPeaks so all tracks share one limit.
 */
export const queueDecodeAudioPeaks = (url, opts) => {
  if (inFlightDecodes.has(url)) return inFlightDecodes.get(url);
  const promise = new Promise((resolve) => {
    waitingDecodes.push(() =>
      decodeAudioPeaks(url, opts).then(resolve, () => resolve(null)),
    );
    pumpDecodes();
  }).then((result) => {
    inFlightDecodes.delete(url);
    return result;
  });
  inFlightDecodes.set(url, promise);
  return promise;
};
